/**
 * Wave 29 Chunk 9b — Recruitment index page.
 *
 * Two columns:
 *   - Open positions (with candidate counts)
 *   - Recent candidates (with best ranking)
 *
 * HR-only. Full-page (DESIGN_CONTRACT Rule 0).
 *
 * Hidro AI is reached through the existing global floating button —
 * we do NOT add a chat surface here.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase, Plus, UserPlus, Loader2, Sparkles, ChevronRight, ListChecks,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import BulkCvUploadButton from "../components/recruitment/BulkCvUploadButton";

const RECO_COLORS = {
  shortlist: "bg-emerald-500/20 text-emerald-300",
  consider: "bg-amber-500/20 text-amber-300",
  pass: "bg-rose-500/20 text-rose-300",
};

export default function Recruitment() {
  const { user } = useAuth();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const [positions, setPositions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !isHr) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const [p, c] = await Promise.all([
          api.get("/recruitment/positions"),
          api.get("/recruitment/candidates"),
        ]);
        if (cancelled) return;
        setPositions(p?.data?.items || []);
        setCandidates(c?.data?.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load recruitment data.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, isHr]);

  // Re-load candidates after bulk upload completes.
  const reloadCandidates = async () => {
    try {
      const c = await api.get("/recruitment/candidates");
      setCandidates(c?.data?.items || []);
    } catch {
      /* silent — toast already fired on per-row failures */
    }
  };

  if (!isHr) {
    return (
      <div className="hv-card p-6 text-sm text-[#dde3ec]" data-testid="recruitment-forbidden">
        Only owners / admins can access recruitment data.
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-6xl" data-testid="recruitment-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-[#1FB6C1]" /> Recruitment
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/recruitment/positions/new" className="hv-btn hv-btn-primary text-xs" data-testid="recruit-new-position">
            <Plus className="w-3.5 h-3.5" /> New position
          </Link>
          <Link to="/recruitment/candidates/new" className="hv-btn hv-btn-ghost text-xs" data-testid="recruit-new-candidate">
            <UserPlus className="w-3.5 h-3.5" /> Manual entry
          </Link>
          <BulkCvUploadButton
            label="Upload CVs"
            variant="ghost"
            onDone={reloadCandidates}
          />
        </div>
      </div>

      <div className="hv-card p-3 text-xs text-[#cbd5e1] flex items-start gap-2" data-testid="recruit-hidro-hint">
        <Sparkles className="w-4 h-4 text-[#1FB6C1] mt-0.5 shrink-0" />
        <span>
          Open the floating Hidro button (bottom right) and say things like:
          <em className="text-white"> "help me define the role for a buyer"</em>,
          <em className="text-white"> "rank the candidates for technician"</em>,
          or <em className="text-white"> "should we hire now or hold?"</em>.
          Hidro will refine requirements, score CVs and cross-reference workload — no extra chat needed.
        </span>
      </div>

      {busy && positions.length === 0 ? (
        <div className="hv-card p-6 flex items-center gap-2 text-[#9aa3b5] text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section className="hv-card overflow-hidden" data-testid="recruit-positions-col">
            <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-[#1FB6C1]" /> Positions
              </h2>
              <span className="text-[10px] text-[#647087]">{positions.length}</span>
            </div>
            {positions.length === 0 ? (
              <div className="p-6 text-[#9aa3b5] text-sm text-center">
                No positions yet —{" "}
                <Link to="/recruitment/positions/new" className="text-[#1FB6C1] hover:underline">create the first one</Link>.
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {positions.map((p) => (
                  <li key={p.id} className="px-4 py-3" data-testid={`recruit-position-${p.id}`}>
                    <Link to={`/recruitment/positions/${p.id}`} className="flex items-center gap-3 group">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-white truncate">{p.title}</span>
                          <span className={"px-1.5 py-0.5 rounded text-[10px] " + (p.status === "open" ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-500/20 text-zinc-300")}>
                            {p.status}
                          </span>
                        </div>
                        <div className="text-[11px] text-[#9aa3b5] mt-1 flex items-center gap-2 flex-wrap">
                          {p.location && <span>{p.location}</span>}
                          {p.department && <span>· {p.department}</span>}
                          <span>· {p.candidate_count || 0} candidate{(p.candidate_count || 0) === 1 ? "" : "s"}</span>
                        </div>
                        {(p.required_skills || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {(p.required_skills || []).slice(0, 6).map((s) => (
                              <span key={s} className="px-1.5 py-0.5 rounded text-[10px] bg-white/5 text-[#dde3ec]">{s}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#647087] group-hover:text-white shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="hv-card overflow-hidden" data-testid="recruit-candidates-col">
            <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-[#1FB6C1]" /> Candidates
              </h2>
              <span className="text-[10px] text-[#647087]">{candidates.length}</span>
            </div>
            {candidates.length === 0 ? (
              <div className="p-6 text-[#9aa3b5] text-sm text-center">
                No candidates yet —{" "}
                <Link to="/recruitment/candidates/new" className="text-[#1FB6C1] hover:underline">upload the first CV</Link>.
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {candidates.map((c) => (
                  <li key={c.id} className="px-4 py-3" data-testid={`recruit-candidate-${c.id}`}>
                    <Link to={`/recruitment/candidates/${c.id}`} className="flex items-center gap-3 group">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-white truncate">{c.full_name || "Unnamed"}</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] bg-white/5 text-[#cbd5e1]">{c.status}</span>
                          {c.best_recommendation && (
                            <span className={"px-1.5 py-0.5 rounded text-[10px] " + (RECO_COLORS[c.best_recommendation] || "bg-white/5 text-[#dde3ec]")}>
                              {c.best_recommendation}
                            </span>
                          )}
                        </div>
                        {c.email && <div className="text-[11px] text-[#9aa3b5] mt-1">{c.email}</div>}
                      </div>
                      {typeof c.best_score === "number" && (
                        <div className="font-mono text-sm text-white shrink-0">{c.best_score}</div>
                      )}
                      <ChevronRight className="w-4 h-4 text-[#647087] group-hover:text-white shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
